import { Descriptions } from 'antd';
import Modal from 'react-bootstrap/Modal';
import { useEffect, useState, useRef } from 'react';
import { FaCircleInfo } from "react-icons/fa6"
import { Tooltip } from 'react-tooltip';
import useDefaultRemark from './useDefaultRemark';
import EditConfirmationModal from '../../../../components/modal/EditConfirmationModal';
import ActionButtonGradient from '../../../../components/button/ActionButtonGradient';

const DefaultRemark = ({
  show,
  handleClose,
  user,
  setCalibratedScore,
  calibratedScore,
  id,
  watch,
  onJustificationSaved
}) => {

  const {
    payload,
    setPayload,
    handleOnChange,
    postDefaultRemark,
    isSaving
  } = useDefaultRemark()

  // Track if data has been initialized to prevent saving empty initial state
  const isInitialized = useRef(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalFormData, setOriginalFormData] = useState(null);
  const loadedFromLocalStorage = useRef(false);
  const [hasExistingJustification, setHasExistingJustification] = useState(false);

  useEffect(() => {
    // Only run when modal opens (show becomes true)
    if (!show) {
      isInitialized.current = false; // Reset when modal closes
      loadedFromLocalStorage.current = false;
      // Reset all state variables when modal closes
      setHasUnsavedChanges(false);
      setOriginalFormData(null);
      setHasExistingJustification(false);
      return;
    }
    
    // Reset all tracking states at the start of each modal open
    loadedFromLocalStorage.current = false;
    setHasUnsavedChanges(false);
    setOriginalFormData(null);
    setHasExistingJustification(false);
    
    const storageKey = `defaultRemark_${calibratedScore[id]?.ProjectID}_${calibratedScore[id]?.EmployeeID}_${calibratedScore[id]?.ProjectPhaseID}`;
    const savedData = localStorage.getItem(storageKey);
    
    // First try to load from localStorage
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        if (parsedData && parsedData.Comment && parsedData.Comment.trim() !== '') {
          setPayload(parsedData);
          loadedFromLocalStorage.current = true;
          // Use setTimeout to ensure payload is updated before marking as initialized
          setTimeout(() => {
            isInitialized.current = true;
          }, 0);
          return;
        }
      } catch (error) {
        console.error('Error loading saved default remark:', error);
      }
    }
    
    // If no saved data, use the original logic (from API/calibratedScore)
    let apiData;
    if (calibratedScore[id].Comment !== "") {
      apiData = {
        ProjectID: calibratedScore[id].ProjectID,
        ProjectPhaseID: calibratedScore[id].ProjectPhaseID,
        EmployeeID: calibratedScore[id].EmployeeID,
        CalibratorID: calibratedScore[id].CalibratorID,
        Comment: calibratedScore[id].Comment,
      };
      // Has existing comment from API
      setHasExistingJustification(true);
    } else{
      apiData = {
        ProjectID: calibratedScore[id].ProjectID,
        ProjectPhaseID: calibratedScore[id].ProjectPhaseID,
        EmployeeID: calibratedScore[id].EmployeeID,
        CalibratorID: calibratedScore[id].CalibratorID,
        Comment: user.FirstComment,
      };
      // Check if FirstComment contains actual data
      setHasExistingJustification(user.FirstComment && user.FirstComment.trim() !== '');
    }
    
    setPayload(apiData);
    // Store original form data from API for comparison
    setOriginalFormData(JSON.parse(JSON.stringify(apiData)));
    
    // Mark as initialized after setting payload
    setTimeout(() => {
      isInitialized.current = true;
    }, 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  // Check if form has been modified from original API data
  useEffect(() => {
    if (originalFormData && payload && !loadedFromLocalStorage.current && isInitialized.current) {
      const formHasChanged = JSON.stringify(payload) !== JSON.stringify(originalFormData);
      setHasUnsavedChanges(formHasChanged);
    } else if (!originalFormData || loadedFromLocalStorage.current) {
      // Reset if no original data or loaded from localStorage
      setHasUnsavedChanges(false);
    }
  }, [payload, originalFormData]);

  // Save to localStorage whenever payload changes (but only after initialization)
  useEffect(() => {
    if (show && isInitialized.current && payload && payload.ProjectID && !hasExistingJustification) {
      // Only save to localStorage if there's no existing justification from API
      const storageKey = `defaultRemark_${payload.ProjectID}_${payload.EmployeeID}_${payload.ProjectPhaseID}`;
      localStorage.setItem(storageKey, JSON.stringify(payload));
    }
  }, [payload, show, hasExistingJustification]);

  const updateState = () => {
    let newPayload = {...calibratedScore}
    newPayload[id].FilledTopBottomMark = true
    newPayload[id].Comment = payload.Comment
    newPayload[id].JustificationType = "default" // Ensure justification type is set
    setCalibratedScore(newPayload)
    
    // Clear localStorage after successful save
    const storageKey = `defaultRemark_${payload.ProjectID}_${payload.EmployeeID}_${payload.ProjectPhaseID}`;
    localStorage.removeItem(storageKey);
    
    // Reset unsaved changes tracking
    setHasUnsavedChanges(false);
    setOriginalFormData(null);
    
    handleClose()
  }

  const handleModalClose = () => {
    // Check if there are unsaved changes and form has existing justification from API
    if (hasUnsavedChanges && hasExistingJustification && !loadedFromLocalStorage.current) {
      setShowUnsavedChangesModal(true);
    } else {
      handleClose();
    }
  };

  const handleConfirmSave = () => {
    // For existing justification (from API): Save to database
    if (hasExistingJustification) {
      setShowUnsavedChangesModal(false); // Dismiss confirmation modal first
      // Clear localStorage after successful save
      const storageKey = `defaultRemark_${payload.ProjectID}_${payload.EmployeeID}_${payload.ProjectPhaseID}`;
      localStorage.removeItem(storageKey);
      postDefaultRemark(updateState); // Save to database (this will also close the main modal)
    } else {
      // For new justification: Save to localStorage as draft
      const storageKey = `defaultRemark_${payload.ProjectID}_${payload.EmployeeID}_${payload.ProjectPhaseID}`;
      localStorage.setItem(storageKey, JSON.stringify(payload));
      
      setShowUnsavedChangesModal(false);
      handleClose();
    }
  };

  const handleDiscardChanges = () => {
    // Remove any draft data from localStorage
    const storageKey = `defaultRemark_${payload.ProjectID}_${payload.EmployeeID}_${payload.ProjectPhaseID}`;
    localStorage.removeItem(storageKey);
    
    setShowUnsavedChangesModal(false);
    setHasUnsavedChanges(false);
    handleClose();
  };

  return (
    <>
      <style>
        {`
          .modern-form-group {
            margin-bottom: 1.5rem;
          }
          .modern-label {
            display: block;
            font-weight: 500;
            font-size: 14px;
            color: #333;
            margin-bottom: 8px;
            text-align: left;
          }
          .required-star {
            color: #dc3545;
            margin-left: 4px;
          }
          .modern-textarea {
            width: 100%;
            padding: 12px;
            border: 1px solid #d9d9d9;
            border-radius: 8px;
            font-size: 14px;
            font-family: inherit;
            resize: vertical;
            transition: border-color 0.3s ease, box-shadow 0.3s ease;
          }
          .modern-textarea:focus {
            outline: none;
            border-color: #02B4CC;
            box-shadow: 0 0 0 2px rgba(2, 180, 204, 0.1);
          }
          .modern-textarea:disabled {
            background-color: #f5f5f5;
            cursor: not-allowed;
          }
          .helper-text {
            font-size: 12px;
            color: #666;
            margin-top: 4px;
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .info-icon-inline {
            color: #02B4CC;
            cursor: help;
          }
        `}
      </style>
      <Modal show={show} onHide={handleModalClose} backdrop="static" centered>
        <Modal.Header closeButton>
          <Modal.Title><b>General Comment</b></Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Descriptions 
            bordered 
            size="small" 
            column={1}
            style={{ marginBottom: '20px' }}
            labelStyle={{ textAlign: 'left' }}
            contentStyle={{ textAlign: 'left' }}
          >
            <Descriptions.Item label="Employee Code">{user.Nik}</Descriptions.Item>
            <Descriptions.Item label="Employee Name">{user.Name}</Descriptions.Item>
            <Descriptions.Item label="Designation">{user.Designation}</Descriptions.Item>
          </Descriptions>
          
          <div className='mt-2'>
            <form onSubmit={(e) => e.preventDefault()}>
              <div className="modern-form-group">
                <label className="modern-label" htmlFor="Comment">
                  General Comment
                </label>
                <textarea
                  disabled={watch}
                  name="Comment"
                  id="Comment"
                  className="modern-textarea"
                  rows={4}
                  placeholder="Provide reasons related to why the employee's rating has changed"
                  onChange={handleOnChange}
                  value={payload.Comment || ''}
                />
                <div className="helper-text">
                  <FaCircleInfo className="info-icon-inline" data-tooltip-id="tooltip-comment" />
                  <span>Provide specific details about the rating justification</span>
                </div>
              </div>
            </form>
          </div>
          <Tooltip
            id="tooltip-comment"
            style={{ 
                backgroundColor: "#efefef", 
                color: "#222", 
                width: "15rem",
                fontSize: "12px",
                padding: "8px 12px",
                borderRadius: "6px",
                boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                textAlign: "left"
            }}
          >
              <span>Provide reasons related to why the employee's rating has changed</span>
          </Tooltip>
        </Modal.Body>
        <Modal.Footer>
          {
            watch ?
            <>
              <ActionButtonGradient
                onClick={handleModalClose}
                variant='danger'
                children={"Close"}
              />
            </>:
            <>
              <ActionButtonGradient
                onClick={() => {
                  const storageKey = `defaultRemark_${payload.ProjectID}_${payload.EmployeeID}_${payload.ProjectPhaseID}`;
                  localStorage.removeItem(storageKey);
                  postDefaultRemark(updateState)
                }}
                variant='primary'
                disabled={isSaving}
                children={isSaving ? "Saving..." : "Save"}
              />
            </>
          }
        </Modal.Footer>
      </Modal>
      
      {/* Unsaved Changes Confirmation Modal */}
      <EditConfirmationModal
        show={showUnsavedChangesModal}
        onHide={() => setShowUnsavedChangesModal(false)}
        onConfirm={handleConfirmSave}
        onCancel={handleDiscardChanges}
        title="Save Changes"
        message="You have made changes to an existing justification. Do you want to save these changes?"
        confirmText="Submit"
        cancelText="Cancel"
      />
    </>
  );
}

export default DefaultRemark